import { MenuItem, TextField } from '@mui/material';
import type { TextFieldProps } from '@mui/material';

import type { PriceListOption } from '../../api/priceListsApi';

export interface RetailEvotorPriceListSelectProps {
  /** The bound list's code, or `''` when the store is not bound to one. */
  value: string;
  onChange: (priceListCode: string) => void;
  priceLists: PriceListOption[];
  label: string;
  /** Shown as the value=`''` placeholder item (e.g. "Not bound"). */
  placeholderLabel: string;
  /** Appended to an inactive list's option text (e.g. "— disabled"). */
  inactiveSuffix: string;
  disabled?: boolean;
  size?: TextFieldProps['size'];
  sx?: TextFieldProps['sx'];
  helperText?: string;
}

/**
 * Evotor-store → price-list bind control (design spec §8.7) — the `RetailEvotorLocationSelect`
 * twin on the price axis. A small, **store-settings-owned** copy (FSD forbids importing a UI
 * component across features): the location select reads `StoreLocationResponse` from this
 * feature's own `storeServiceApi`; this one reads `PriceListOption` from this feature's own local
 * `priceListsApi.ts` — both ultimately proxy a different feature's backing service, but the
 * pattern (and the reason for the local copy) is identical.
 */
export function RetailEvotorPriceListSelect({
  value,
  onChange,
  priceLists,
  label,
  placeholderLabel,
  inactiveSuffix,
  disabled,
  size = 'small',
  sx,
  helperText,
}: RetailEvotorPriceListSelectProps) {
  return (
    <TextField
      select
      label={label}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      disabled={disabled}
      size={size}
      sx={sx}
      helperText={helperText}
      slotProps={{ select: { displayEmpty: true } }}
    >
      <MenuItem value="">
        <em>{placeholderLabel}</em>
      </MenuItem>
      {priceLists.map((list) => (
        <MenuItem key={list.code} value={list.code} disabled={!list.active}>
          {list.name}
          {!list.active ? ` — ${inactiveSuffix}` : ''}
        </MenuItem>
      ))}
    </TextField>
  );
}
